/**
 * /frontend/src/features/analytics/pages/QueueAnalyticsPage.js
 */

import React, { useEffect, useMemo, useState } from 'react';
import useQueueAnalytics from '../hooks/useQueueAnalytics';
import LoadingSkeleton from '../components/shared/LoadingSkeleton';
import ErrorState from '../components/shared/ErrorState';
import EmptyState from '../components/shared/EmptyState';
import GranularitySelector from '../components/shared/GranularitySelector';
import ZoneSectionFilter from '../components/shared/ZoneSectionFilter';
import QueuePerformanceKPIs from '../components/queue/QueuePerformanceKPIs';
import WaitTimeTrendChart from '../components/queue/WaitTimeTrendChart';
import ThroughputByHourChart from '../components/queue/ThroughputByHourChart';
import ServiceTimeBySection from '../components/queue/ServiceTimeBySection';
import QueueOutcomePieChart from '../components/queue/QueueOutcomePieChart';
import './QueueAnalyticsPage.css';

const GRANULARITIES = ['HOURLY', 'DAILY', 'WEEKLY', 'MONTHLY'];

/**
 * Queue analytics page.
 * @param {{request:Function,storeToken:string,storeUuid:string,startDate:string,endDate:string,refreshSignal?:unknown,onSyncingChange?:Function}} props
 * @returns {JSX.Element}
 */
const QueueAnalyticsPage = ({ request, storeToken, storeUuid, startDate, endDate, refreshSignal, onSyncingChange }) => {
  const [zoneId, setZoneId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [granularity, setGranularity] = useState('DAILY');
  const hook = useQueueAnalytics({ request, storeToken, storeUuid, startDate, endDate, zoneId, sectionId, granularity, refreshSignal });
  const performance = hook.data?.performance || null;
  const trend = hook.data?.trend || null;
  const syncing = hook.isLoading || hook.isRefreshing;

  const isEmpty = useMemo(() => !performance && !trend, [performance, trend]);

  useEffect(() => {
    if (typeof onSyncingChange !== 'function') return () => null;
    onSyncingChange(syncing);
    return () => onSyncingChange(false);
  }, [onSyncingChange, syncing]);

  if (hook.isLoading) return <LoadingSkeleton />;
  if (hook.isError) return <ErrorState onRetry={hook.refetch} />;
  if (isEmpty) return <EmptyState message="No queue analytics data for the selected period." />;

  return (
    <div className="analytics-page-stack">
      <QueuePerformanceKPIs performance={performance} />

      <div className="analytics-page-grid-2">
        <ZoneSectionFilter
          zoneId={zoneId}
          sectionId={sectionId}
          zones={[]}
          sections={[]}
          onZoneChange={setZoneId}
          onSectionChange={setSectionId}
        />
        <GranularitySelector value={granularity} options={GRANULARITIES} onChange={setGranularity} />
      </div>

      <WaitTimeTrendChart trend={trend} />

      <div className="analytics-page-grid-2">
        <ThroughputByHourChart performance={performance} />
        <QueueOutcomePieChart performance={performance} />
      </div>

      <ServiceTimeBySection performance={performance} />
    </div>
  );
};

export default QueueAnalyticsPage;
